import type { StatusEffect } from "./Stats";

export type TargetKind = "enemy" | "all-enemies" | "ally" | "all-allies" | "self" | "none";

export type SkillKind = "damage" | "heal" | "status" | "flee" | "revive";

export interface SkillDef {
  id: string;
  name: string;
  /** MP cost. */
  mp: number;
  kind: SkillKind;
  target: TargetKind;
  /** Base magnitude for damage/heal. */
  power?: number;
  /** Fraction of `power` the roll may vary by. 0.2 means ±20%. */
  variance?: number;
  /** Ignores defense — most spells do. */
  ignoresDefense?: boolean;
  status?: StatusEffect;
  statusTurns?: number;
  /** 0..1. */
  statusChance?: number;
  description?: string;
  /** Usable outside battle (healing, escape ropes…). */
  field?: boolean;
  /** Usable inside battle. Defaults to true. */
  battle?: boolean;
  /** Sound effect name played when it resolves. */
  sound?: string;
  /** Tint for the on-screen effect. */
  color?: number;
  /** Overrides the default "<user> casts <skill>!" line. */
  castMessage?: (user: string) => string;
}

export interface ItemDef {
  id: string;
  name: string;
  description: string;
  price: number;
  /** Consumables reference a skill for their effect. */
  use?: string;
  consumable?: boolean;
  kind?: "consumable" | "weapon" | "armor" | "key";
  attack?: number;
  defense?: number;
  agility?: number;
  maxHp?: number;
}

/** Simple keyed registry with a helpful error when content is missing. */
export class Registry<T extends { id: string }> {
  private items = new Map<string, T>();

  constructor(private label: string, defs: readonly T[] = []) {
    for (const def of defs) this.add(def);
  }

  add(def: T): this {
    this.items.set(def.id, def);
    return this;
  }

  addAll(defs: readonly T[]): this {
    for (const def of defs) this.add(def);
    return this;
  }

  get(id: string): T {
    const item = this.items.get(id);
    if (!item) throw new Error(`Unknown ${this.label}: ${id}`);
    return item;
  }

  find(id: string): T | undefined {
    return this.items.get(id);
  }

  all(): T[] {
    return [...this.items.values()];
  }
}
